import * as types from 'ducks/types'
import { ActionType } from 'ducks/common'

import { QuickGuiddeLayerType, QuickGuiddeType, Shape, TextShapeType } from 'app/types'
import { uuid } from 'modules'

type StateType = {
    shortcutsDisabled: boolean
    activeStep: number
    showDnd: boolean
    selectedLayerId: string | null
    copiedLayer: null | QuickGuiddeLayerType
    steps: Array<QuickGuiddeType>
}

const initialState: StateType = {
    shortcutsDisabled: false,
    showDnd: false,
    activeStep: 0,
    selectedLayerId: null,
    copiedLayer: null,
    steps: []
}

export const qgEditorReducer = (state = initialState, action: ActionType): StateType => {
    const { activeStep, steps, selectedLayerId, copiedLayer } = state

    switch (action.type) {
        case types.SET_ARROW_DIRECTION: {
            const { data, dataKey } = action.payload

            const newSteps = steps.map((step, stepIndex) => {
                if (stepIndex !== activeStep) return step

                return {
                    ...step,
                    layers: step.layers.map(layer => {
                        if (layer.id !== selectedLayerId) return layer

                        return {
                            ...layer,
                            data,
                            dataKey
                        }
                    })
                }
            })

            return {
                ...state,
                steps: newSteps
            }
        }
        case types.SET_QUICK_GUIDDE_STEP_LAYER: {
            const { layerId, layer } = action.payload

            const newSteps = steps.map((step, stepIndex) => {
                if (stepIndex !== activeStep) return step

                return {
                    ...step,
                    layers: step.layers.map(oldLayer => {
                        if (oldLayer.id === layerId) {
                            return layer
                        } else {
                            return oldLayer
                        }
                    })
                }
            })

            return {
                ...state,
                steps: newSteps
            }
        }

        case types.SET_QUICK_GUIDDE_STEPS: {
            return {
                ...initialState,
                ...action.payload,
                activeStep
            }
        }

        case types.SET_QUICK_GUIDDE_DURATION: {
            const { duration, applyToAll } = action.payload

            const newSteps = steps.map((step, stepIndex) => {
                // Change Duration of current step even if it has audio
                if (stepIndex === activeStep) return { ...step, duration }

                // Don't change duration of other steps which has audio or applyToAll is not enabled
                if (!applyToAll || Boolean(step.audioNote)) return step

                // Change all other steps which has no audio and applyToAll is enabled
                return { ...step, duration }
            })

            return {
                ...state,
                steps: newSteps
            }
        }

        case types.SET_DND_AREA_VISIBILITY: {
            return {
                ...state,
                showDnd: action.payload
            }
        }

        case types.SET_ACTIVE_STEP: {
            return {
                ...state,
                activeStep: action.payload,
                selectedLayerId: ''
            }
        }

        case types.SET_LAYER_POSITION: {
            const { idx, x, y } = action.payload

            const newSteps = JSON.parse(JSON.stringify(steps))

            newSteps[activeStep].layers[idx].x = x
            newSteps[activeStep].layers[idx].y = y

            return {
                ...state,
                steps: newSteps
            }
        }

        case types.SET_LAYER_TRANSFORM: {
            const { idx, x, y, width, height, scaleX, scaleY, rotation, radius, fontSize } =
                action.payload

            const newSteps = JSON.parse(JSON.stringify(steps))
            newSteps[activeStep].layers[idx].x = x
            newSteps[activeStep].layers[idx].y = y
            newSteps[activeStep].layers[idx].rotation = rotation

            if (width) {
                newSteps[activeStep].layers[idx].width = width
            }
            if (height) {
                newSteps[activeStep].layers[idx].height = height
            }
            if (scaleX && scaleY) {
                newSteps[activeStep].layers[idx].scaleX = scaleX
                newSteps[activeStep].layers[idx].scaleY = scaleY
            }
            if (radius) {
                newSteps[activeStep].layers[idx].radius = radius
            }
            if (fontSize) {
                newSteps[activeStep].layers[idx].fontSize = fontSize
            }

            return {
                ...state,
                steps: newSteps
            }
        }

        case types.SET_LAYER_TEXT: {
            const { layerId, title, height } = action.payload

            let layerIndex = -1
            const stepIndex = steps.findIndex(step =>
                step.layers.some((layer, index) => {
                    if (layer.id === layerId) {
                        layerIndex = index
                        return true
                    }
                })
            )

            if (stepIndex === -1 || layerIndex === -1) {
                return state
            }

            const newSteps = JSON.parse(JSON.stringify(steps))
            newSteps[stepIndex].layers[layerIndex].title = title
            if (height) {
                newSteps[stepIndex].layers[layerIndex].height = height
            }

            return {
                ...state,
                steps: newSteps
            }
        }

        case types.TOGGLE_SPOTLIGHT: {
            const newSteps = steps.map((step, stepIndex) => {
                if (stepIndex !== activeStep) return step
                return {
                    ...step,
                    layers: step.layers.map(layer => {
                        if (layer.id !== selectedLayerId) return layer
                        if (layer.type === Shape.Rectangle || layer.type === Shape.Circle) {
                            return { ...layer, isSpotlight: !layer?.isSpotlight }
                        }
                        return layer
                    })
                }
            })

            return {
                ...state,
                steps: newSteps
            }
        }

        case types.ADD_STEP_NUMBERING: {
            const { text, circle } = action.payload

            const newSteps = steps.map((step, stepIndex) => {
                if (step.kind === 'cover' || step.kind === 'end') return step

                // if no cover first index will be 00 so need to add 1
                // if there's a cover, regular step will start at 01
                const base = steps[0]?.kind === 'cover' ? 0 : 1
                const index = (stepIndex + base).toString().padStart(2, '0')

                const textLayer = step.layers.find(layer => layer.type === Shape.Text) as
                    | TextShapeType
                    | undefined

                const isComplexBg = step.layers.some(layer => layer.type == Shape.BrowserBar)
                const scale = isComplexBg ? 0.8 : 1

                // Required available space for each side
                const { innerWidth: canvasWidth, innerHeight: canvasHeight } = step.windowDimensions

                const padding = 20
                const requiredSpace = padding * 2 + circle.radius * 2 * scale

                const choosePosition = () => {
                    const defaultPosition = { x: padding, y: padding }
                    if (!textLayer) return defaultPosition

                    // Left
                    if (textLayer.x > requiredSpace) {
                        return {
                            x: textLayer.x - (requiredSpace - padding),
                            y: textLayer.y
                        }
                    }
                    // Top
                    if (textLayer.y > requiredSpace) {
                        return {
                            x: textLayer.x,
                            y: textLayer.y - (requiredSpace - padding)
                        }
                    }
                    // Bottom
                    if (canvasHeight - textLayer.height > requiredSpace) {
                        return {
                            x: textLayer.x,
                            y: textLayer.y + textLayer.height + padding
                        }
                    }
                    // Right
                    if (canvasWidth - (textLayer.x + textLayer.width) > requiredSpace) {
                        return {
                            x: textLayer.x + textLayer.width + padding,
                            y: textLayer.y
                        }
                    }

                    return defaultPosition
                }

                return {
                    ...step,
                    layers: [
                        ...step.layers,
                        {
                            ...action.payload,
                            scaleX: scale,
                            scaleY: scale,
                            text: { ...text, title: index },
                            ...choosePosition()
                        }
                    ]
                }
            })

            return {
                ...state,
                steps: newSteps
            }
        }

        case types.REMOVE_STEP_NUMBERING: {
            const newSteps = steps.map(step => {
                if (step.kind === 'cover' || step.kind === 'end') return step
                return {
                    ...step,
                    layers: step.layers.filter(step => step.type !== Shape.CircleWithText)
                }
            })

            return {
                ...state,
                steps: newSteps
            }
        }

        case types.SET_STEP_NUMBERING_TEXT: {
            const { index, title } = action.payload

            const newSteps = JSON.parse(JSON.stringify(steps))
            newSteps[activeStep].layers[index].text.title = title

            return {
                ...state,
                steps: newSteps
            }
        }

        case types.SET_BACKGROUND_COLOR: {
            const {
                fill,
                fillLinearGradientColorStops,
                fillLinearGradientEndPoint,
                fillLinearGradientStartPoint
            } = action.payload

            const newSteps = steps.map((step, stepIndex) => {
                if (stepIndex !== activeStep) return step
                return {
                    ...step,
                    layers: step.layers.map(layer => {
                        if (layer.type === Shape.Rectangle && layer.isBackground) {
                            if (fill) {
                                return {
                                    ...layer,
                                    fill,
                                    fillLinearGradientColorStops: undefined,
                                    fillLinearGradientStartPoint: undefined,
                                    fillLinearGradientEndPoint: undefined
                                }
                            }

                            return {
                                ...layer,
                                fillLinearGradientColorStops,
                                fillLinearGradientStartPoint,
                                fillLinearGradientEndPoint,
                                fill: undefined
                            }
                        }

                        return layer
                    })
                }
            })

            return {
                ...state,
                steps: newSteps
            }
        }

        case types.SET_BACKGROUND_IMAGE: {
            const newSteps = steps.map((step, stepIndex) => {
                if (stepIndex !== activeStep) return step
                return {
                    ...step,
                    layers: step.layers.map(layer => {
                        if (layer.type === Shape.Image && layer.isBackground) {
                            return {
                                ...layer,
                                // overwrite existing background image layer
                                ...action.payload
                            }
                        }
                        return layer
                    })
                }
            })

            return {
                ...state,
                steps: newSteps
            }
        }

        case types.ADD_BACKGROUND_LAYER: {
            const newSteps = steps.map((step, stepIndex) => {
                if (stepIndex !== activeStep) return step

                const hasBrowserBar = step.layers.find(layer => layer.type === Shape.BrowserBar)

                return {
                    ...step,
                    layers: [
                        action.payload,
                        // When we change background image or color - we need to remove previous background + all the blur layers
                        ...step.layers.filter(
                            layer =>
                                !layer.hasOwnProperty('isBackground') &&
                                layer.type !== Shape.Blur &&
                                layer.type !== Shape.BrowserBar &&
                                (hasBrowserBar ? layer.type !== Shape.Overlay : true)
                        )
                    ]
                }
            })

            return {
                ...state,
                steps: newSteps
            }
        }

        case types.ADD_OVERLAY_LAYER: {
            const newSteps = steps.map((step, stepIndex) => {
                if (stepIndex !== activeStep) return step

                const hasBgImageLayer = step.layers.find(
                    layer => layer.type === Shape.Image && layer.isBackground
                )

                const hasBgRectLayer = step.layers.find(
                    layer => layer.type === Shape.Rectangle && layer.isBackground
                )

                const index = 0 + (hasBgImageLayer ? 1 : 0) + (hasBgRectLayer ? 1 : 0)

                return {
                    ...step,
                    layers: [
                        // part of the array before the specified index
                        ...step.layers.slice(0, index),
                        // inserted item
                        action.payload,
                        // part of the array after the specified index
                        ...step.layers.slice(index)
                    ]
                }
            })

            return {
                ...state,
                steps: newSteps
            }
        }

        case types.ADD_NEW_LAYER: {
            const newSteps = steps.map((step, stepIndex) => {
                if (stepIndex !== activeStep) return step
                return {
                    ...step,
                    layers: [...step.layers, action.payload]
                }
            })

            return {
                ...state,
                steps: newSteps
            }
        }

        case types.SET_FONT_SIZE: {
            const newSteps = steps.map((step, stepIndex) => {
                if (stepIndex !== activeStep) return step

                return {
                    ...step,
                    layers: step.layers.map(layer => {
                        if (layer.id !== selectedLayerId) return layer

                        return {
                            ...layer,
                            fontSize: action.payload
                        }
                    })
                }
            })

            return {
                ...state,
                steps: newSteps
            }
        }

        case types.SET_TEXT_HEIGHT: {
            const { id, height } = action.payload
            const newSteps = steps.map((step, stepIndex) => {
                if (stepIndex !== activeStep) return step

                return {
                    ...step,
                    layers: step.layers.map(layer => {
                        if (layer.id !== id) return layer

                        return {
                            ...layer,
                            height
                        }
                    })
                }
            })

            return {
                ...state,
                steps: newSteps
            }
        }

        case types.SET_TEXT_COLORS: {
            const { color, backgroundColor } = action.payload

            const newSteps = steps.map((step, stepIndex) => {
                if (stepIndex !== activeStep) return step

                return {
                    ...step,
                    layers: step.layers.map(layer => {
                        if (layer.id !== selectedLayerId) return layer

                        return {
                            ...layer,
                            ...(color ? { color } : {}),
                            ...(backgroundColor ? { backgroundColor } : {})
                        }
                    })
                }
            })

            return {
                ...state,
                steps: newSteps
            }
        }

        case types.MOVE_SELECTED_LAYER: {
            if (!selectedLayerId) return state

            const newSteps = steps.map((step, stepIndex) => {
                if (stepIndex !== activeStep) return step

                const newLayers = step.layers.map(layer => {
                    if (layer.id !== selectedLayerId) return layer

                    const direction = action.payload.direction
                    if (direction === 'TOP') layer.y -= 10
                    if (direction === 'RIGHT') layer.x += 10
                    if (direction === 'BOTTOM') layer.y += 10
                    if (direction === 'LEFT') layer.x -= 10

                    return {
                        ...layer
                    }
                })

                return {
                    ...step,
                    layers: newLayers
                }
            })

            return {
                ...state,
                steps: newSteps
            }
        }

        case types.COPY_SELECTED_LAYER: {
            if (!selectedLayerId) return state

            const layer = (JSON.parse(JSON.stringify(steps)) as Array<QuickGuiddeType>).reduce(
                (acc, curr) => {
                    if (Object.keys(acc).length !== 0) return acc
                    const selectedLayer = curr.layers.find(layer => layer.id === selectedLayerId)

                    return { ...selectedLayer }
                },
                {}
            ) as QuickGuiddeLayerType

            return {
                ...state,
                copiedLayer: Object.keys(layer).length === 0 ? null : layer
            }
        }

        case types.PASTE_SELECTED_LAYER: {
            if (!copiedLayer) return state

            const newSteps = steps.map((step, stepIndex) => {
                if (stepIndex !== activeStep) return step
                const isOriginalStep = step.layers.find(layer => layer.id === copiedLayer.id)

                return {
                    ...step,
                    layers: [
                        ...step.layers,
                        {
                            ...copiedLayer,
                            id: uuid(),
                            x: copiedLayer.x + (isOriginalStep ? 100 : 0),
                            y: copiedLayer.y + (isOriginalStep ? 100 : 0)
                        }
                    ]
                }
            })

            return {
                ...state,
                steps: newSteps
            }
        }

        case types.DUPLICATE_SELECTED_LAYER: {
            const newSteps = steps.map((step, stepIndex) => {
                if (stepIndex !== activeStep) return step
                const index = step.layers.findIndex(layer => layer.id === selectedLayerId)

                return {
                    ...step,
                    layers: [
                        ...step.layers.slice(0, index + 1),
                        {
                            ...step.layers[index],
                            x: step.layers[index].x + 100,
                            y: step.layers[index].y + 100,
                            id: uuid(),
                            isBackground: undefined
                        },
                        ...step.layers.slice(index + 1)
                    ]
                }
            })

            return {
                ...state,
                steps: newSteps
            }
        }

        case types.REMOVE_SELECTED_LAYER: {
            if (!selectedLayerId) return state
            let layerExist = false // we should not remove selected layer from the step which is not current

            const newSteps = steps.map((step, stepIndex) => {
                if (stepIndex !== activeStep) return step
                layerExist = step.layers.some(layer => layer.id === selectedLayerId)
                return {
                    ...step,
                    layers: step.layers.filter(layer => layer.id !== selectedLayerId)
                }
            })

            return {
                ...state,
                steps: newSteps,
                selectedLayerId: layerExist ? null : selectedLayerId
            }
        }

        case types.SET_STEP: {
            const newSteps = steps.map((step, stepIndex) => {
                if (stepIndex !== activeStep) return step
                return action.payload
            })

            return {
                ...state,
                steps: newSteps
            }
        }

        case types.ADD_COVER_STEP: {
            return {
                ...state,
                steps: [{ ...action.payload }, ...steps]
            }
        }

        case types.ADD_ENDING_STEP: {
            return {
                ...state,
                steps: [...steps, { ...action.payload, isChanged: true }]
            }
        }

        case types.ADD_NEW_STEP: {
            // If current step is ending - we need to insert new step before it
            const delta = steps[activeStep].kind === 'end' ? 0 : 1

            const newSteps = [
                ...steps.slice(0, activeStep + delta),
                action.payload,
                ...steps.slice(activeStep + delta).map((step, stepIndex) => {
                    return {
                        ...step,
                        // update step numbering if the one exists
                        layers: step.layers.map(layer => {
                            if (layer.type !== Shape.CircleWithText) return layer

                            return {
                                ...layer,
                                text: {
                                    ...layer.text,
                                    title: (activeStep + 2 + stepIndex).toString().padStart(2, '0') // update step numbering of all the steps after newly added
                                }
                            }
                        })
                    }
                })
            ]

            return {
                ...state,
                steps: newSteps,
                activeStep: Math.min(activeStep + 1, steps.length)
            }
        }

        case types.DUPLICATE_ACTIVE_STEP: {
            const newSteps = [
                ...steps.slice(0, activeStep + 1),
                {
                    ...steps[activeStep],
                    id: uuid(), // need to overwrite step's id to be unique
                    layers: steps[activeStep].layers.map(layer => ({ ...layer, id: uuid() })), // need to overwrite every layer's id to be unique
                    isChanged: true
                },
                ...steps.slice(activeStep + 1)
            ]

            return {
                ...state,
                steps: newSteps,
                activeStep: Math.min(activeStep + 1, steps.length)
            }
        }

        case types.REMOVE_ACTIVE_STEP: {
            // remove step and update step numbering if the one exists
            const newSteps = steps
                .filter((_, idx) => idx !== activeStep)
                .map((step, stepIndex) => {
                    return {
                        ...step,
                        layers: step.layers.map(layer => {
                            if (layer.type !== Shape.CircleWithText) return layer

                            // if no cover first index will be 00 so need to add 1
                            // if there's a cover, regular step will start at 01
                            const base = steps[0]?.kind === 'cover' ? 0 : 1
                            const index = (stepIndex + base).toString().padStart(2, '0')

                            return {
                                ...layer,
                                text: {
                                    ...layer.text,
                                    title: index // update step numbering of all the steps after newly added
                                }
                            }
                        })
                    }
                })

            const newIndex =
                activeStep === steps.length - 1 ? Math.max(0, activeStep - 1) : activeStep

            return {
                ...state,
                activeStep: newIndex,
                steps: newSteps
            }
        }

        case types.SELECT_LAYER: {
            return {
                ...state,
                selectedLayerId: action.payload
            }
        }

        case types.SET_TEMP_QUICK_GUIDDE_AUDIO_NOTE: {
            const newSteps = steps.map((step, stepIndex) => {
                if (stepIndex !== activeStep) return step
                return {
                    ...step,
                    tempAudioNote: action.payload
                }
            })

            return {
                ...state,
                steps: newSteps
            }
        }

        case types.SET_QUICK_GUIDDE_AUDIO_NOTE: {
            const newSteps = steps.flatMap((step, stepIndex) => {
                if (stepIndex !== activeStep) return step

                const defaultDuration = 3 // @TODO HARDCODED DEFAULT STEP DURATION
                const stepDuration = step.duration || defaultDuration
                const newDuration = action.payload
                    ? Math.max(stepDuration, action.payload.audioDuration)
                    : stepDuration

                return {
                    ...step,
                    duration: newDuration,
                    // If we remove audioNote, we should remove audioCircle layer as well
                    layers: step.layers.filter(layer =>
                        action.payload ? true : layer.type !== Shape.AudioCircle
                    ),
                    audioNote: action.payload
                }
            })

            return {
                ...state,
                steps: newSteps
            }
        }

        case types.ENABLE_QG_SHORTCUTS: {
            return {
                ...state,
                shortcutsDisabled: false
            }
        }

        case types.DISABLE_QG_SHORTCUTS: {
            return {
                ...state,
                shortcutsDisabled: true
            }
        }

        case types.RESET_STEPS:
            return initialState

        default: {
            return state
        }
    }
}
